import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";
import { Layout } from "../components/Layout";
import { Seo } from "../components/Seo";
import { Breadcrumbs } from "../components/Breadcrumbs";
import { Reveal, MaskedLines } from "../components/motion";
import { ScoreBadge } from "../components/Affiliate";
import { VpnLogo } from "../components/VpnLogo";
import { CtaBanner } from "../components/CtaBanner";
import { VPNS, getVpn } from "../data/vpns";
import { COMPARISONS } from "../data/comparisons";
import { BEST_FOR } from "../data/bestFor";
import { RANKINGS } from "../data/rankings";
import { LEARN } from "../data/learn";

const HubHero = ({ eyebrow, lines, sub }) => (
  <section className="grain relative overflow-hidden bg-[#0F172A]">
    <div className="hero-grid absolute inset-0" />
    <div className="relative mx-auto max-w-7xl px-6 py-20 lg:px-8">
      <p className="mb-4 text-xs font-bold uppercase tracking-[0.25em] text-[#7EAAFF]">{eyebrow}</p>
      <h1 className="font-display text-4xl font-black tracking-tighter text-white sm:text-5xl lg:text-6xl">
        <MaskedLines lines={lines} />
      </h1>
      <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-300">{sub}</p>
    </div>
  </section>
);

const HubCard = ({ to, title, sub, testId, index = 0 }) => (
  <Reveal delay={index * 0.04}>
    <Link
      to={to}
      data-testid={testId}
      className="group flex h-full flex-col bg-white p-8 transition-colors duration-200 hover:bg-[#0F172A]"
    >
      <h2 className="font-display text-lg font-extrabold tracking-tight text-[#0F172A] transition-colors duration-200 group-hover:text-white">
        {title}
      </h2>
      <p className="mt-2 flex-1 text-sm leading-relaxed text-slate-500 transition-colors duration-200 group-hover:text-slate-300">
        {sub}
      </p>
      <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-[0.15em] text-[#4687FF]">
        Read <ArrowRight size={13} className="transition-transform duration-200 group-hover:translate-x-1" />
      </span>
    </Link>
  </Reveal>
);

const Grid = ({ children }) => (
  <section className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
    <div className="grid gap-px border border-slate-200 bg-slate-200 sm:grid-cols-2 lg:grid-cols-3">{children}</div>
  </section>
);

export const ReviewsIndex = () => (
  <Layout>
    <Seo
      title="VPN Reviews 2026: In-Depth, Independently Tested | VPNReviews.ai"
      description="Full, hands-on reviews of NordVPN, Surfshark, PureVPN, VeePN and AdGuard VPN — speed tests, streaming results, security analysis and verdicts."
    />
    <HubHero eyebrow="Reviews" lines={["VPN Reviews,", "Tested Hands-On"]} sub="Every review is the product of weeks of real-world testing — speed benchmarks, streaming verification, leak tests, and daily use." />
    <Breadcrumbs crumbs={[{ label: "Reviews" }]} />
    <section className="mx-auto max-w-5xl space-y-6 px-6 py-16 lg:px-8">
      {VPNS.map((v, i) => (
        <Reveal key={v.slug} delay={i * 0.05}>
          <Link
            to={`/reviews/${v.slug}`}
            data-testid={`reviews-index-card-${v.slug}`}
            className="group flex flex-col gap-6 border border-slate-200 bg-white p-8 transition-transform duration-300 hover:-translate-y-1 hover:shadow-lg sm:flex-row sm:items-center"
          >
            <div className="flex items-center gap-5">
              <span className="font-display text-4xl font-black tracking-tighter text-slate-200">{String(v.rank).padStart(2, "0")}</span>
              <VpnLogo vpn={v} size={72} />
              <ScoreBadge score={v.score} />
            </div>
            <div className="flex-1">
              <h2 className="font-display text-2xl font-extrabold tracking-tight text-[#0F172A] group-hover:text-[#4687FF]">
                {v.name} Review 2026
              </h2>
              <p className="mt-2 text-sm leading-relaxed text-slate-600">{v.tagline}</p>
            </div>
            <ArrowRight size={20} className="hidden shrink-0 text-[#4687FF] transition-transform duration-200 group-hover:translate-x-1 sm:block" />
          </Link>
        </Reveal>
      ))}
    </section>
    <CtaBanner />
  </Layout>
);

export const CompareIndex = () => (
  <Layout>
    <Seo
      title="VPN Comparisons 2026: Head-to-Head Battles | VPNReviews.ai"
      description="NordVPN vs Surfshark, Surfshark vs PureVPN and 8 more head-to-head VPN comparisons — identical test suites, honest winners."
    />
    <HubHero eyebrow="Compare" lines={["Head-to-Head", "VPN Comparisons"]} sub="Ten direct match-ups, one identical test suite. Every comparison names a clear winner — backed by data." />
    <Breadcrumbs crumbs={[{ label: "Compare" }]} />
    <Grid>
      {COMPARISONS.map((c, i) => (
        <HubCard
          key={c.slug}
          to={`/compare/${c.slug}`}
          testId={`compare-index-${c.slug}`}
          title={`${getVpn(c.a).name} vs ${getVpn(c.b).name}`}
          sub={c.verdict.slice(0, 110) + "…"}
          index={i}
        />
      ))}
    </Grid>
    <CtaBanner />
  </Layout>
);

export const BestForIndex = () => (
  <Layout>
    <Seo
      title="Best VPN For Every Use Case (2026) | VPNReviews.ai"
      description="Streaming, gaming, torrenting, travel, privacy, and more — we ranked the top 5 VPNs for 12 specific use cases. Find the right VPN for your needs."
    />
    <HubHero eyebrow="Best VPN For..." lines={["The Right VPN,", "For Your Exact Need"]} sub="Twelve use cases, each with a purpose-built ranking. NordVPN wins most — but not all." />
    <Breadcrumbs crumbs={[{ label: "Best VPN For" }]} />
    <Grid>
      {BEST_FOR.map((b, i) => (
        <HubCard
          key={b.slug}
          to={`/best-vpn-for/${b.slug}`}
          testId={`bestfor-index-${b.slug}`}
          title={`Best VPN for ${b.label}`}
          sub={b.intro[0].slice(0, 110) + "…"}
          index={i}
        />
      ))}
    </Grid>
    <CtaBanner />
  </Layout>
);

export const RankingsIndex = () => (
  <Layout>
    <Seo
      title="VPN Rankings 2026: Fastest, Best Value, Best Privacy & More"
      description="Seven data-driven VPN rankings for 2026 — fastest VPN, best value, best privacy, best streaming, best security features, best UX, and overall."
    />
    <HubHero eyebrow="Rankings" lines={["The 2026 VPN", "Leaderboards"]} sub="Seven single-metric rankings, from raw speed to audit-verified privacy. See exactly where each VPN excels — and where it doesn't." />
    <Breadcrumbs crumbs={[{ label: "Rankings" }]} />
    <Grid>
      {RANKINGS.map((r, i) => (
        <HubCard
          key={r.slug}
          to={`/rankings/${r.slug}`}
          testId={`rankings-index-${r.slug}`}
          title={r.title}
          sub={r.intro.slice(0, 110) + "…"}
          index={i}
        />
      ))}
    </Grid>
    <CtaBanner />
  </Layout>
);

export const LearnIndex = () => (
  <Layout>
    <Seo
      title="VPN Learning Center: Plain-English Guides | VPNReviews.ai"
      description="What is a VPN? How do protocols work? What's a kill switch? Seven plain-English guides that make VPN technology genuinely understandable."
    />
    <HubHero eyebrow="Learning Center" lines={["VPN Technology,", "Explained Simply"]} sub="No jargon, no scare tactics. Seven guides that explain exactly how VPNs work and what actually matters." />
    <Breadcrumbs crumbs={[{ label: "Learn" }]} />
    <Grid>
      {LEARN.map((l, i) => (
        <HubCard
          key={l.slug}
          to={`/learn/${l.slug}`}
          testId={`learn-index-${l.slug}`}
          title={l.title}
          sub={l.intro.slice(0, 110) + "…"}
          index={i}
        />
      ))}
    </Grid>
    <CtaBanner />
  </Layout>
);
